// React
import React, { useState } from 'react';

// Components
import AnimateButton from 'components/@extended/AnimateButton';
import InputFieldErrorMessage from 'components/common/InputFieldErrorMessage';
import Select from 'components/common/Select';
import CountryCodeSelect from '../common/CountryCodeSelect';

// Packages
import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Grid, TextField, Stack, InputLabel, Button, Tooltip } from '@mui/material';

// Utils
import { nameRegExp, errorMessage } from 'utils/constants';
import { capitalizeFirstLetterMulti } from 'utils/Helpers';

const AddOccupantsForm = ({
    occupants,
    setOccupants,
    editingOccupant,
    loading,
    handleAddOrUpdateOccupant,
    setStep,
    setEditingOccupant
}) => {
    const types = [
        { key: 'owner', value: 'owner', name: 'Owner' },
        { key: 'occupant', value: 'occupant', name: 'Occupant' },
        { key: 'minor', value: 'minor', name: 'Minor' }
    ];
    const [selectedCountry, setSelectedCountry] = useState(null);
    const typeDescriptions = {
        owner: 'Primary resident or property owner',
        occupant: 'Adult resident living at the address',
        minor: 'Child or dependent under 16'
    };

    const normalizePhoneCode = code => Number.parseInt(code, 10) || 1;

    const defaultValues = {
        first_name: '',
        last_name: '',
        phone: { code: 1, country: 'US', number: '' },
        type: ''
    };

    const initialValues = editingOccupant
        ? {
              first_name: editingOccupant.first_name
                  ? capitalizeFirstLetterMulti(editingOccupant.first_name)
                  : '',
              last_name: editingOccupant.last_name
                  ? capitalizeFirstLetterMulti(editingOccupant.last_name)
                  : '',
              phone: {
                  number: `${editingOccupant.phone?.number}` || '',
                  code: normalizePhoneCode(editingOccupant.phone?.code),
                  country: editingOccupant.phone?.country || 'US'
              },
              type: editingOccupant.type || ''
          }
        : defaultValues;

    const validationSchema = Yup.object().shape({
        first_name: Yup.string()
            .trim()
            .required(errorMessage.firstName.required)
            .matches(nameRegExp.format, errorMessage.firstName.valid),

        last_name: Yup.string()
            .trim()
            .required(errorMessage.lastName.required)
            .matches(nameRegExp.format, errorMessage.lastName.valid),

        phone: Yup.object().shape({
            code: Yup.number().required(),
            country: Yup.string().required(),
            number: Yup.string().trim().required(errorMessage.phone.required)
        }),
        type: Yup.string().trim().required(errorMessage.type.required)
    });

    const validatePhoneNumber = ({ phone }) => {
        const { code, country, number } = phone || {};
        const phoneSchema = Yup.string().phone(country);

        if (!number) return {};

        try {
            phoneSchema.validateSync(`${code}${number}`);
            return {};
        } catch {
            return {
                phone: {
                    number: 'Phone number needs to be valid'
                }
            };
        }
    };

    const handleSubmitOccupant = (values, { resetForm, setFieldError }) => {
        const trimValue = val => (typeof val === 'string' ? val.trim() : val);

        // National number length varies by country (e.g. BR mobiles are 11 digits);
        // libphonenumber also strips a typed country code / trunk prefix.
        const digits = values.phone.number.replace(/[^0-9]/g, '');
        const parsed = parsePhoneNumberFromString(digits, values.phone.country);
        const normalizedPhone = parseInt(parsed?.nationalNumber || digits);

        const isEditing = editingOccupant !== null;
        const currentPhone = editingOccupant?.phone?.number;

        const phoneExists = occupants.some(o => {
            const number = o.phone?.number;
            return number === normalizedPhone && (!isEditing || number !== currentPhone);
        });

        if (phoneExists) {
            setFieldError(
                'phone.number',
                'This phone number is already used by another occupant.'
            );
            return;
        }

        const occupant = {
            first_name: trimValue(values.first_name).toLowerCase(),
            last_name: trimValue(values.last_name).toLowerCase(),
            phone: {
                number: normalizedPhone,
                code: normalizePhoneCode(values.phone.code),
                country: values.phone.country
            },
            email: false,
            type: values.type
        };

        handleAddOrUpdateOccupant(occupant);
        resetForm();
    };

    const isFormEmpty = values => {
        const first = (values.first_name || '').trim();
        const last = (values.last_name || '').trim();
        const phoneNum = (values.phone?.number || '').trim();
        const type = (values.type || '').trim();

        return (
            first.length === 0 &&
            last.length === 0 &&
            phoneNum.length === 0 &&
            type.length === 0
        );
    };

    return (
        <Formik
            initialValues={initialValues}
            validateOnChange
            validateOnBlur
            enableReinitialize
            validate={validatePhoneNumber}
            validationSchema={validationSchema}
            onSubmit={handleSubmitOccupant}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                resetForm,
                validateField
            }) => {
                const formEmpty = isFormEmpty(values);

                const showFinished =
                    occupants.length > 0 && formEmpty && editingOccupant === null;

                const showClearOrCancel = !formEmpty;

                const showThirdButton = showFinished || showClearOrCancel;

                const thirdButtonLabel = showFinished
                    ? 'Finished'
                    : editingOccupant
                    ? 'Cancel'
                    : 'Clear';

                const handleThirdButtonClick = () => {
                    if (showFinished) {
                        setStep('review');
                        return;
                    }

                    if (editingOccupant) {
                        setEditingOccupant(null);
                        resetForm({ values: defaultValues });
                        return;
                    }
                    resetForm({ values: defaultValues });
                };

                return (
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={0.5}>
                                    <InputLabel>First Name *</InputLabel>
                                    <TextField
                                        name="first_name"
                                        fullWidth
                                        placeholder="Enter first name"
                                        value={values.first_name}
                                        onChange={e =>
                                            setFieldValue(
                                                'first_name',
                                                capitalizeFirstLetterMulti(e.target.value)
                                            )
                                        }
                                        onBlur={handleBlur}
                                        error={touched.first_name && !!errors.first_name}
                                        helperText={
                                            touched.first_name && errors.first_name
                                        }
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={0.5}>
                                    <InputLabel>Last Name *</InputLabel>
                                    <TextField
                                        name="last_name"
                                        fullWidth
                                        placeholder="Enter last name"
                                        value={values.last_name}
                                        onChange={e =>
                                            setFieldValue(
                                                'last_name',
                                                capitalizeFirstLetterMulti(e.target.value)
                                            )
                                        }
                                        onBlur={handleBlur}
                                        error={touched.last_name && !!errors.last_name}
                                        helperText={touched.last_name && errors.last_name}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Stack spacing={0.5}>
                                    <InputLabel htmlFor="country">
                                        Country Code *
                                    </InputLabel>
                                    <CountryCodeSelect
                                        label=""
                                        value={selectedCountry}
                                        defaultRegion={values.phone?.country || 'US'}
                                        onChange={selectedCountry => {
                                            if (!selectedCountry) return;

                                            setSelectedCountry(selectedCountry);

                                            setFieldValue(
                                                'phone.code',
                                                normalizePhoneCode(selectedCountry.code)
                                            );

                                            setFieldValue(
                                                'phone.country',
                                                selectedCountry.region
                                            );

                                            setTimeout(() => {
                                                validateField('phone.number');
                                            });
                                        }}
                                        size="medium"
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <Stack spacing={0.5}>
                                    <InputLabel>Mobile Phone *</InputLabel>
                                    <TextField
                                        name="phone.number"
                                        fullWidth
                                        placeholder="Enter mobile phone"
                                        inputMode="numeric"
                                        onChange={e =>
                                            setFieldValue(
                                                'phone.number',
                                                e.target.value.replace(/\D/g, '')
                                            )
                                        }
                                        value={values.phone.number}
                                        onBlur={handleBlur}
                                        error={
                                            touched.phone?.number &&
                                            !!errors.phone?.number
                                        }
                                        helperText={
                                            touched.phone?.number && errors.phone?.number
                                        }
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={0.5}>
                                    <InputLabel htmlFor="type">Type *</InputLabel>
                                    <Select
                                        label="Type"
                                        name="type"
                                        value={values.type}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        touched={touched.type}
                                        error={errors.type}
                                        displayEmpty
                                        renderValue={selected => {
                                            const found = types.find(
                                                t => t.value === selected
                                            );
                                            if (!found) {
                                                return (
                                                    <span style={{ color: '#9e9e9e' }}>
                                                        Select Type
                                                    </span>
                                                );
                                            }
                                            return found.name;
                                        }}
                                        options={types.map(type => ({
                                            key: type.value,
                                            value: type.value,
                                            display: (
                                                <Tooltip
                                                    title={typeDescriptions[type.value]}
                                                    placement="right"
                                                    arrow
                                                >
                                                    <span>{type.name}</span>
                                                </Tooltip>
                                            )
                                        }))}
                                    />
                                    <InputFieldErrorMessage
                                        touched={touched.type}
                                        error={errors.type}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <AnimateButton>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        className="oeGoBack"
                                        onClick={() => setStep('address')}
                                        disabled={editingOccupant !== null}
                                        size="large"
                                    >
                                        Go Back
                                    </Button>
                                </AnimateButton>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <AnimateButton disabled={loading || formEmpty}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        type="submit"
                                        className="oeOccupant"
                                        disabled={loading || formEmpty}
                                        size="large"
                                    >
                                        {editingOccupant
                                            ? 'Update Occupant'
                                            : 'Add Occupant'}
                                    </Button>
                                </AnimateButton>
                            </Grid>
                            {showThirdButton && (
                                <Grid item xs={12} sm={4}>
                                    <AnimateButton>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className={
                                                showFinished ? undefined : 'oeClearCancel'
                                            }
                                            onClick={handleThirdButtonClick}
                                            size="large"
                                        >
                                            {thirdButtonLabel}
                                        </Button>
                                    </AnimateButton>
                                </Grid>
                            )}
                        </Grid>
                    </form>
                );
            }}
        </Formik>
    );
};

export default AddOccupantsForm;
